"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Copy, Maximize2 } from "lucide-react";
import { Control, Controller } from "react-hook-form";
import { Compartment, EditorState, Prec } from "@codemirror/state";
import {
  EditorView,
  keymap,
  placeholder as cmPlaceholder,
  tooltips,
  type ViewUpdate,
} from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { bracketMatching, syntaxHighlighting } from "@codemirror/language";
import { json } from "@codemirror/lang-json";
import { xml } from "@codemirror/lang-xml";
import { python } from "@codemirror/lang-python";
import { sql, PostgreSQL } from "@codemirror/lang-sql";
import { classHighlighter } from "@lezer/highlight";
import "./codeeditor.css";
import { cn } from "@/lib/utils";
import { SpotToast } from "@/components/SpotToast";
import { NoxusIconButton } from "@/components/UI/NoxusIconButton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { Separator } from "@/components/shadcn/separator";
import { getModifierKey } from "@/utils/generic";
import {
  inputsToNodeOptions,
  SlashVariableHint,
} from "@/components/Form/ComplexMarkdownField/ComplexMarkdownField";
import { InsertVariableDropdown } from "@/components/Form/ComplexMarkdownField/Toolbar";
import { useEditorStoreSync } from "@/components/Form/ComplexMarkdownField/useEditorStoreSync";
import type { EditorInput } from "@/components/Form/ComplexMarkdownField/types";
import {
  filterVariableOptions,
  flattenVariableOptions,
  VariablePickerPanel,
} from "@/components/SpotflowV2/Sidebar/VariablePickerPanel";
import { formatVarRefToken } from "@/components/SpotflowV2/Sidebar/variableRefs";
import { variableChips } from "./variableChipExtension";
import { jsonWithVarsLinter, xmlWithVarsLinter } from "./varsLinters";
import { CodeEditorDialog } from "./CodeEditorDialog";

export type CodeEditorLanguage = "json" | "xml" | "text" | "sql" | "python";

function languageExtensions(language: CodeEditorLanguage) {
  switch (language) {
    case "json":
      return [json(), jsonWithVarsLinter()];
    case "xml":
      return [xml(), xmlWithVarsLinter()];
    case "sql":
      // Postgres dialect matches the tables engine; upper-case keyword hints.
      return [sql({ dialect: PostgreSQL, upperCaseKeywords: true })];
    case "python":
      return [python()];
    default:
      return [];
  }
}

const LANGUAGE_LABELS: Record<CodeEditorLanguage, string> = {
  json: "JSON",
  xml: "XML",
  text: "Raw text",
  sql: "SQL",
  python: "Python",
};

export interface CodeEditorHandle {
  insertVariable: (id: string) => void;
}

interface CodeEditorProps {
  control: Control<Record<string, unknown>>;
  name: string;
  placeholder?: string;
  isDisabled?: boolean;
  inputs: EditorInput[];
  invalid?: boolean;
  onBlur?: () => void;
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
  language?: CodeEditorLanguage;
  minRows?: number;
  dialogTitle?: string;
  overlay?: React.ReactNode;
}

export function CodeEditor(props: CodeEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field: { value, onChange, onBlur: fieldOnBlur } }) => {
        const stringValue = typeof value === "string" ? value : "";
        const handleBlur = () => {
          fieldOnBlur();
          props.onBlur?.();
        };
        return (
          <div className="w-full">
            <CodeEditorSurface
              value={stringValue}
              onChange={onChange}
              placeholder={props.placeholder}
              isDisabled={props.isDisabled}
              inputs={props.inputs}
              invalid={props.invalid}
              onBlur={handleBlur}
              nodeId={props.nodeId}
              variableBindings={props.variableBindings}
              language={props.language}
              minRows={props.minRows}
              onExpand={
                props.isDisabled ? undefined : () => setIsExpanded(true)
              }
              overlay={props.overlay}
            />
            {props.inputs.length > 0 && <SlashVariableHint />}
            <CodeEditorDialog
              isOpen={isExpanded}
              onOpenChange={setIsExpanded}
              initialValue={stringValue}
              onCommit={onChange}
              inputs={props.inputs}
              label={props.dialogTitle}
              placeholder={props.placeholder}
              isDisabled={props.isDisabled}
              language={props.language}
              nodeId={props.nodeId}
              variableBindings={props.variableBindings}
            />
          </div>
        );
      }}
    />
  );
}

interface CodeEditorSurfaceProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  inputs: EditorInput[];
  invalid?: boolean;
  onBlur?: () => void;
  nodeId?: string;
  variableBindings?: Record<string, string | undefined>;
  language?: CodeEditorLanguage;
  minRows?: number;
  onExpand?: () => void;
  fillHeight?: boolean;
  overlay?: React.ReactNode;
  // Focus the editor once it mounts (a list row created by "Add item").
  autoFocus?: boolean;
}

type SlashState =
  | { open: false }
  | {
      open: true;
      slashPos: number;
      x: number;
      y: number;
      caretTop: number;
      query: string;
    };

const SLASH_MENU_WIDTH = 330;
const SLASH_MENU_MARGIN = 8;

// 1.4rem per line (0.875rem mono × 1.6 line-height) + 1rem .cm-content padding.
function codeRowsToRem(rows: number): string {
  return `${(rows * 1.4 + 1).toFixed(2)}rem`;
}

const keepFocus = (e: React.MouseEvent) => e.preventDefault();

export const CodeEditorSurface = forwardRef<
  CodeEditorHandle,
  CodeEditorSurfaceProps
>(function CodeEditorSurface(
  {
    value,
    onChange,
    placeholder,
    isDisabled,
    inputs,
    invalid,
    onBlur,
    nodeId,
    variableBindings,
    language,
    minRows,
    onExpand,
    fillHeight,
    overlay,
    autoFocus,
  },
  ref
) {
  const codeLanguage = language ?? "text";

  const reactId = useId();
  const editorId = useMemo(
    () => `code-editor-${reactId.replace(/:/g, "_")}`,
    [reactId]
  );

  useEditorStoreSync(editorId, inputs, nodeId, variableBindings);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const onBlurRef = useRef(onBlur);
  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  const lastValueRef = useRef(value);
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const [slash, setSlashState] = useState<SlashState>({ open: false });
  const [slashIndex, setSlashIndex] = useState(0);

  const variableOptions = useMemo(() => inputsToNodeOptions(inputs), [inputs]);

  const filteredSlashOptions = useMemo(() => {
    if (!slash.open) return [];
    return filterVariableOptions(variableOptions, slash.query);
  }, [slash, variableOptions]);
  const filteredSlashItems = useMemo(
    () => flattenVariableOptions(filteredSlashOptions),
    [filteredSlashOptions]
  );

  // Mirrored into refs so the once-created CodeMirror handlers never read
  // stale state.
  const slashRef = useRef(slash);
  const setSlash = useCallback((next: SlashState) => {
    slashRef.current = next;
    setSlashState(next);
  }, []);
  const slashIndexRef = useRef(slashIndex);
  useEffect(() => {
    slashIndexRef.current = slashIndex;
  }, [slashIndex]);
  const filteredRef = useRef(filteredSlashItems);
  useEffect(() => {
    filteredRef.current = filteredSlashItems;
  }, [filteredSlashItems]);

  const slashKey = slash.open ? slash.query : null;
  useEffect(() => {
    setSlashIndex(0);
  }, [slashKey]);

  const commitSlash = useCallback(
    (id: string) => {
      const view = viewRef.current;
      const s = slashRef.current;
      if (!view || !s.open) return;
      const to = Math.max(s.slashPos + 1, view.state.selection.main.head);
      const token = `${formatVarRefToken(id)} `;
      view.dispatch({
        changes: { from: s.slashPos, to, insert: token },
        selection: { anchor: s.slashPos + token.length },
      });
      view.focus();
      setSlash({ open: false });
    },
    [setSlash]
  );
  const commitSlashRef = useRef(commitSlash);
  useEffect(() => {
    commitSlashRef.current = commitSlash;
  }, [commitSlash]);

  const refreshSlash = useCallback(
    (state: EditorState) => {
      const s = slashRef.current;
      if (!s.open) return;
      const sel = state.selection.main;
      const slashEnd = s.slashPos + 1;
      if (
        !sel.empty ||
        sel.head < slashEnd ||
        slashEnd > state.doc.length ||
        state.doc.sliceString(s.slashPos, slashEnd) !== "/"
      ) {
        setSlash({ open: false });
        return;
      }
      const query = state.doc.sliceString(slashEnd, sel.head);
      if (/\s/.test(query)) {
        setSlash({ open: false });
        return;
      }
      if (query !== s.query) setSlash({ ...s, query });
    },
    [setSlash]
  );

  const editableCompartment = useMemo(() => new Compartment(), []);
  const languageCompartment = useMemo(() => new Compartment(), []);

  useEffect(() => {
    const parent = containerRef.current;
    if (!parent) return;

    const slashKeymap = Prec.highest(
      keymap.of([
        {
          key: "Escape",
          run: () => {
            if (!slashRef.current.open) return false;
            setSlash({ open: false });
            return true;
          },
        },
        {
          key: "ArrowDown",
          run: () => {
            if (!slashRef.current.open) return false;
            const items = filteredRef.current;
            if (items.length === 0) return false;
            setSlashIndex((i) => (i + 1) % items.length);
            return true;
          },
        },
        {
          key: "ArrowUp",
          run: () => {
            if (!slashRef.current.open) return false;
            const items = filteredRef.current;
            if (items.length === 0) return false;
            setSlashIndex((i) => (i - 1 + items.length) % items.length);
            return true;
          },
        },
        ...["Enter", "Tab"].map((key) => ({
          key,
          run: () => {
            if (!slashRef.current.open) return false;
            const picked = filteredRef.current[slashIndexRef.current];
            if (picked) commitSlashRef.current(picked.value);
            else setSlash({ open: false });
            return true;
          },
        })),
      ])
    );

    const updateListener = EditorView.updateListener.of(
      (update: ViewUpdate) => {
        if (update.docChanged) {
          const text = update.state.doc.toString();
          if (text !== lastValueRef.current) {
            lastValueRef.current = text;
            onChangeRef.current(text);
          }
          const sel = update.state.selection.main;
          if (!slashRef.current.open && sel.empty) {
            let slashTyped = false;
            update.changes.iterChanges(
              (_fromA, _toA, _fromB, toB, inserted) => {
                // A lone typed "/" only — pastes ending in "/" must not
                // open the menu.
                if (toB === sel.head && inserted.toString() === "/") {
                  slashTyped = true;
                }
              }
            );
            if (slashTyped) {
              const pos = sel.head - 1;
              update.view.requestMeasure({
                read: (view) => {
                  const coords = view.coordsAtPos(sel.head);
                  if (!coords) return;
                  setSlash({
                    open: true,
                    slashPos: pos,
                    x: coords.left,
                    y: coords.bottom,
                    caretTop: coords.top,
                    query: "",
                  });
                },
              });
              return;
            }
          }
        }
        if (
          slashRef.current.open &&
          (update.docChanged || update.selectionSet)
        ) {
          refreshSlash(update.state);
        }
      }
    );

    const view = new EditorView({
      state: EditorState.create({
        doc: valueRef.current,
        extensions: [
          history(),
          slashKeymap,
          keymap.of([...defaultKeymap, ...historyKeymap]),
          cmPlaceholder(placeholder ?? ""),
          EditorView.lineWrapping,
          // min-height on .cm-content (not the wrapper) so clicking anywhere
          // in the visible area focuses the editor.
          ...(minRows != null && !fillHeight
            ? [
                EditorView.contentAttributes.of({
                  style: `min-height: ${codeRowsToRem(minRows)}`,
                }),
              ]
            : []),
          variableChips(editorId),
          bracketMatching(),
          // Lint tooltips render into <body> so the expand dialog can't clip
          // them (a first-line diagnostic flips its tooltip upward).
          tooltips({ position: "fixed", parent: document.body }),
          languageCompartment.of(languageExtensions(codeLanguage)),
          syntaxHighlighting(classHighlighter),
          editableCompartment.of(EditorView.editable.of(!isDisabled)),
          updateListener,
          EditorView.domEventHandlers({
            blur: () => {
              setSlash({ open: false });
              onBlurRef.current?.();
              return false;
            },
          }),
        ],
      }),
      parent,
    });
    viewRef.current = view;
    lastValueRef.current = valueRef.current;
    if (autoFocus) view.focus();

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // Created once; everything dynamic flows through refs or compartments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorId]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: editableCompartment.reconfigure(
        EditorView.editable.of(!isDisabled)
      ),
    });
  }, [isDisabled, editableCompartment]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: languageCompartment.reconfigure(
        languageExtensions(codeLanguage)
      ),
    });
  }, [codeLanguage, languageCompartment]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: value },
    });
  }, [value]);

  const insertVariableFromBar = useCallback((id: string) => {
    const view = viewRef.current;
    if (!view) return;
    const { from, to } = view.state.selection.main;
    const token = `${formatVarRefToken(id)} `;
    view.dispatch({
      changes: { from, to, insert: token },
      selection: { anchor: from + token.length },
    });
    view.focus();
  }, []);

  useImperativeHandle(ref, () => ({ insertVariable: insertVariableFromBar }), [
    insertVariableFromBar,
  ]);

  const slashMenuRef = useRef<HTMLDivElement | null>(null);
  const [slashMenuTop, setSlashMenuTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!slash.open) {
      setSlashMenuTop(null);
      return;
    }
    const el = slashMenuRef.current;
    if (!el) return;
    const height = el.offsetHeight;
    let top = slash.y + 4;
    if (top + height > window.innerHeight - SLASH_MENU_MARGIN) {
      top = slash.caretTop - height - 4;
      if (top < SLASH_MENU_MARGIN) {
        top = Math.max(
          SLASH_MENU_MARGIN,
          window.innerHeight - height - SLASH_MENU_MARGIN
        );
      }
    }
    setSlashMenuTop(top);
  }, [slash, filteredSlashItems]);

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-md border bg-background overflow-hidden",
        invalid
          ? "border-amber-400 focus-within:ring-1 focus-within:ring-amber-400/70"
          : "border-input focus-within:ring-1 focus-within:ring-ring",
        fillHeight && "h-full flex-1",
        isDisabled && "opacity-60 pointer-events-none"
      )}
    >
      <div
        aria-disabled={isDisabled}
        className="flex items-center justify-between gap-4 border-b border-input bg-background2 px-3 py-1.5"
        onMouseDown={keepFocus}
      >
        {/* Kept when empty so justify-between still right-aligns the buttons.
            "Raw text" is no syntax at all, so only real languages get a badge. */}
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {language && language !== "text" && LANGUAGE_LABELS[language]}
        </span>
        <div className="flex items-center gap-2">
          {inputs.length > 0 && (
            <>
              <InsertVariableDropdown
                options={variableOptions}
                onSelect={insertVariableFromBar}
                isDisabled={isDisabled}
              />
              <Separator orientation="vertical" />
            </>
          )}
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <NoxusIconButton
                  variant="tertiary"
                  icon={Copy}
                  onClick={() => {
                    navigator.clipboard.writeText(valueRef.current ?? "");
                    SpotToast.success("Copied to clipboard");
                  }}
                  isDisabled={isDisabled}
                  tabIndex={-1}
                />
              </TooltipTrigger>
              <TooltipContent side="top">
                {`Copy (${getModifierKey()}C)`}
              </TooltipContent>
            </Tooltip>
            {onExpand && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <NoxusIconButton
                    variant="tertiary"
                    icon={Maximize2}
                    onClick={onExpand}
                    isDisabled={isDisabled}
                    tabIndex={-1}
                  />
                </TooltipTrigger>
                <TooltipContent side="top">Expand</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      <div
        ref={containerRef}
        className={cn(
          "noxus-code-editor",
          language === "text" && "noxus-code-editor-plain",
          fillHeight && "noxus-code-editor-fill flex-1 min-h-0 overflow-auto"
        )}
      />
      {slash.open && (
        <div
          ref={slashMenuRef}
          data-slash-menu
          role="listbox"
          className="fixed z-50 w-[330px] rounded-md border bg-popover text-popover-foreground shadow-md overflow-hidden"
          style={{
            left: Math.max(
              SLASH_MENU_MARGIN,
              Math.min(
                slash.x,
                window.innerWidth - SLASH_MENU_WIDTH - SLASH_MENU_MARGIN
              )
            ),
            top: slashMenuTop ?? slash.y + 4,
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <VariablePickerPanel
            options={variableOptions}
            search={slash.query}
            showSearch={false}
            activeIndex={Math.min(
              slashIndex,
              Math.max(filteredSlashItems.length - 1, 0)
            )}
            onActiveIndexChange={setSlashIndex}
            onSelect={({ value: id }) => commitSlash(id)}
          />
        </div>
      )}
      {overlay}
    </div>
  );
});
